import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import type { UserRole } from './types/gateway';
import type { CorrectionFormData } from './components/training';
import { useAuth } from './hooks/useAuth';
import { useGateway } from './hooks/useGateway';
import { useSessions } from './hooks/useSessions';
import { useMessages } from './hooks/useMessages';
import { useKnowledge } from './hooks/useKnowledge';
import { useThreads } from './hooks/useThreads';
import { useThreadAcl } from './hooks/useThreadAcl';
import { useThreadEmails } from './hooks/useThreadEmails';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { ChatArea } from './components/ChatArea';
import { InputBar } from './components/InputBar';
import { ComposeGuard, type PendingDraft } from './components/ComposeGuard';
import { ShareModal } from './components/ShareModal';
import { AdminUserPanel } from './components/AdminUserPanel';
import { FeedbackTriagePanel, useOpenFeedbackCount } from './components/FeedbackTriagePanel';
import { BrowserUseKeyPanel } from './components/BrowserUseKeyPanel';
import type { ThreadAcl } from './types/gateway';
import { getRoleConfig } from './config/roles';
import TrainingPage from './pages/TrainingPage';
import HelpCenterPage from './pages/HelpCenterPage';
import LoginPage from './pages/LoginPage';
import styles from './App.module.css';

const ROLE_STORAGE_KEY = 'foreman-active-role';
const VALID_ROLES: UserRole[] = ['field', 'designer', 'accounting', 'operations', 'admin'];

function readStoredRole(): UserRole | null {
  try {
    const raw = localStorage.getItem(ROLE_STORAGE_KEY);
    if (raw && (VALID_ROLES as string[]).includes(raw)) return raw as UserRole;
  } catch {
    /* ignore */
  }
  return null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

function AuthenticatedApp() {
  const { user, isAdmin } = useAuth();
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);

  // Poll open-feedback count for the TopBar badge (admin-only).
  const openFeedbackCount = useOpenFeedbackCount(isAdmin);

  const [role, setRole] = useState<UserRole>(() => {
    const stored = readStoredRole();
    if (stored && stored !== 'admin') return stored;
    const userRole = user?.uiRole;
    if (userRole && userRole !== 'admin') return userRole;
    return 'field';
  });

  useEffect(() => {
    try {
      localStorage.setItem(ROLE_STORAGE_KEY, role);
    } catch {
      /* ignore */
    }
  }, [role]);

  const [sidebarTab, setSidebarTab] = useState<'threads' | 'knowledge'>('threads');

  const { status, send } = useGateway();

  // Thread ACL — source of truth for what's visible.
  const {
    acl,
    users: roster,
    loading: aclLoading,
    refresh: refreshAcl,
    createAcl,
    shareAcl,
    renameAcl,
    deleteAcl: deleteAclRow,
    createInviteLink,
    acceptInvite,
  } = useThreadAcl(user?.username);

  // Share modal state
  const [shareTarget, setShareTarget] = useState<ThreadAcl | null>(null);

  // One-time: consume any ?invite=TOKEN in the URL.
  useEffect(() => {
    if (!user?.username || aclLoading) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    if (!token) return;
    acceptInvite(token)
      .then((res) => {
        history.replaceState({}, '', window.location.pathname);
        // Auto-switch to the thread we just joined. MUST go through
        // switchSession (the single source of truth) — the old
        // setActiveThreadId was a no-op whenever a session was active, so the
        // joined thread never actually opened.
        if (res.thread?.sessionKey) {
          switchSession(res.thread.sessionKey);
        }
      })
      .catch((err) => {
        console.error('[App] invite accept failed:', err);
        alert(err.message || 'Could not accept invite');
        history.replaceState({}, '', window.location.pathname);
      });
    // switchSession is stable from useSessions; we intentionally only run on auth + acl ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, aclLoading]);

  // Sessions filtered against the ACL.
  const { sessions, activeSessionId, createSession, switchSession, loadingHistory, chatHistory } = useSessions({
    username: user?.username || null,
    acl,
    createAcl,
    aclLoading,
  });

  // One-time: consume a ?thread=KEY deep link — a direct link to a specific
  // chat (e.g. emailed to a teammate). Waits until the user's sessions are
  // loaded and the thread is actually accessible to them, then opens it through
  // switchSession (the single source of truth) and cleans the URL. If the thread
  // isn't visible to this user, nothing happens (they land on the default view).
  const threadLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (threadLinkConsumedRef.current) return;
    if (!user?.username || aclLoading) return;
    const key = new URLSearchParams(window.location.search).get('thread');
    if (!key) { threadLinkConsumedRef.current = true; return; }
    // Wait for the target thread to appear in the session list before switching.
    if (!sessions.some((s) => s.id === key)) return;
    switchSession(key);
    threadLinkConsumedRef.current = true;
    history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, user?.username, aclLoading]);

  // Threads grouped by ownership. activeThreadId is a read-only mirror of
  // activeSessionId (single source of truth) — mutations go via switchSession.
  const { myThreads, sharedWithMe, activeThreadId } = useThreads(
    sessions,
    acl,
    user?.username || null,
    activeSessionId
  );

  // Display name of the open thread — used for the print header / PDF filename.
  const activeThreadName = useMemo(() => {
    const t = [...myThreads, ...sharedWithMe].find((th) => th.id === activeSessionId);
    return t?.name || 'Foreman chat';
  }, [myThreads, sharedWithMe, activeSessionId]);

  // Print / Save-as-PDF the current view. The print stylesheet (print.css)
  // reveals only the element tagged [data-print-active], un-clips it out of the
  // flex layout so the WHOLE thing paginates, and hides the app chrome. We tag
  // the right region here (the chat conversation, or the knowledge base when
  // that sidebar tab is open) and swap document.title so the browser's print
  // header and default "Save as PDF" filename read sensibly.
  const handlePrint = useCallback(() => {
    const region = sidebarTab === 'knowledge' ? 'knowledge' : 'chat';
    const el = document.querySelector(`[data-print-region="${region}"]`);
    if (!el) return;
    const prevTitle = document.title;
    document.title = region === 'knowledge'
      ? 'Foreman — Knowledge base'
      : `Foreman — ${activeThreadName}`;
    el.setAttribute('data-print-active', '');
    const restore = () => {
      el.removeAttribute('data-print-active');
      document.title = prevTitle;
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);
    window.print();
    setTimeout(restore, 1500); // fallback if afterprint never fires
  }, [activeThreadName, sidebarTab]);

  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleSelectThread = useCallback(
    (id: string) => {
      switchSession(id);
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [isMobile, switchSession]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const { entries: knowledgeEntries, searchKnowledge, teachMode, toggleTeachMode, submitCorrection } = useKnowledge({ send, activeSessionId });

  const { messages, isStreaming, sendMessage } = useMessages({
    activeSessionId,
    teachMode,
    chatHistory,
    loadingHistory,
    role,
    senderIdentity: user ? { username: user.username, name: user.name } : null,
  });

  const { emails: threadEmails } = useThreadEmails(activeSessionId);

  // Auto-invite: when the user @-mentions a teammate not on the thread ACL,
  // add them with read+write before the message goes out.
  const autoInviteMentions = useCallback(
    async (sessionKey: string | null, mentions: string[]) => {
      if (!sessionKey || mentions.length === 0) return;
      const acl = await refreshAcl().then(() => undefined).catch(() => undefined);
      void acl;
      // Look up the latest ACL after refresh.
      const current = await fetch('/api/auth/threads', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('foreman-auth-token') || ''}`,
        },
      }).then((r) => r.ok ? r.json() : { threads: [] }).catch(() => ({ threads: [] }));
      const target = (current.threads as ThreadAcl[]).find((t) => t.sessionKey === sessionKey);
      if (!target || target.owner !== user?.username) return; // only owner can mutate ACL
      if (target.sharedWith === '*') return; // org-wide already covers everyone
      const already = new Set([target.owner, ...(Array.isArray(target.sharedWith) ? target.sharedWith : [])]);
      const toAdd = mentions.filter((m) => !already.has(m));
      if (toAdd.length === 0) return;
      const next = [...(target.sharedWith as string[]), ...toAdd];
      try {
        await shareAcl(sessionKey, next);
      } catch (err) {
        console.error('[App] auto-invite failed:', err);
      }
    },
    [refreshAcl, shareAcl, user?.username]
  );

  // ── Shared-thread attribution + compose guard ───────────────────────────────
  // Derived ownership state for the ACTIVE thread. Drives (a) author chips on
  // user messages and (b) the "this isn't your thread" compose guard.
  const activeAcl = useMemo(
    () => (activeSessionId ? acl.find((a) => a.sessionKey === activeSessionId) : undefined),
    [acl, activeSessionId]
  );
  // Shared = anyone besides me can read it. Used for chip rendering AND to
  // decide whether history stamps get verified (see useSessions).
  const activeThreadShared =
    !!activeAcl &&
    (activeAcl.owner !== user?.username ||
      activeAcl.sharedWith === '*' ||
      (Array.isArray(activeAcl.sharedWith) && activeAcl.sharedWith.length > 0));
  // FAIL-SAFE: if the ACL row for the active thread is unknown (missing or
  // never loaded), we can't prove the user owns it — treat as not-owned so
  // the guard shows. We deliberately do NOT gate on `aclLoading`: refreshes
  // (e.g. after an @-mention auto-invite) flip it true while the existing
  // rows are still in state, and keying off it would flash the guard inside
  // the user's own threads. A brand-new thread lands in `acl` synchronously
  // via createAcl, so this only bites genuinely unknown threads.
  const activeThreadOwnedByMe =
    !!activeAcl && !!user?.username && activeAcl.owner === user.username;
  const guardActive = !!activeSessionId && !activeThreadOwnedByMe;
  const ownerDisplayName = useMemo(() => {
    const owner = activeAcl?.owner;
    if (!owner) return 'another teammate';
    return roster.find((u) => u.username === owner)?.name || owner;
  }, [activeAcl?.owner, roster]);

  // Draft held while the first-send confirm is showing. Cleared on thread
  // switch — the confirm is about THIS thread; carrying it across a manual
  // switch would risk posting into a thread the user never looked at.
  const [pendingSend, setPendingSend] = useState<PendingDraft | null>(null);
  const [guardBusy, setGuardBusy] = useState(false);
  useEffect(() => {
    setPendingSend(null);
    setGuardBusy(false);
  }, [activeSessionId]);

  // One explicit "Post to {owner}'s thread" per thread per browser session —
  // after that, sends go straight through (the banner stays as a reminder).
  const guardAckKey = (sessionKey: string) => `foreman-guard-ack:${sessionKey}`;
  const hasGuardAck = (sessionKey: string): boolean => {
    try {
      return sessionStorage.getItem(guardAckKey(sessionKey)) === '1';
    } catch {
      return false;
    }
  };
  const setGuardAck = (sessionKey: string) => {
    try {
      sessionStorage.setItem(guardAckKey(sessionKey), '1');
    } catch {
      /* private-mode storage failure — worst case, we confirm again */
    }
  };

  /** The actual dispatch — shared by the direct path and both confirm choices. */
  const doSend = useCallback(
    (content: string, files?: File[], mentions?: string[]) => {
      sendMessage(content, files);
      if (mentions && mentions.length > 0) {
        autoInviteMentions(activeSessionId, mentions);
      }
    },
    [sendMessage, autoInviteMentions, activeSessionId]
  );

  const handleSend = useCallback(
    (content: string, files?: File[], mentions?: string[]) => {
      // First send into a thread the user doesn't own → hold the draft and ask.
      if (guardActive && activeSessionId && !hasGuardAck(activeSessionId)) {
        // If the user ignores the confirm and sends again, MERGE into the held
        // draft rather than replacing it — the first message must never be
        // silently dropped.
        setPendingSend((prev) =>
          prev
            ? {
                content: [prev.content, content].filter((t) => t.trim()).join('\n\n'),
                files: [...(prev.files || []), ...(files || [])],
                mentions: Array.from(new Set([...(prev.mentions || []), ...(mentions || [])])),
              }
            : { content, files, mentions }
        );
        return;
      }
      doSend(content, files, mentions);
    },
    [doSend, guardActive, activeSessionId]
  );

  /** Confirm choice: post the held draft into the owner's thread. Remembered
   *  for the rest of the browser session so we don't nag on every message. */
  const handleGuardPostAnyway = useCallback(() => {
    if (!pendingSend) return;
    if (activeSessionId) setGuardAck(activeSessionId);
    const draft = pendingSend;
    setPendingSend(null);
    doSend(draft.content, draft.files, draft.mentions);
  }, [pendingSend, activeSessionId, doSend]);

  /** Confirm choice: spin up a fresh thread (existing creation path — ACL row
   *  + switch) and carry the typed draft into it. */
  const handleGuardStartOwnThread = useCallback(async () => {
    if (!pendingSend) return;
    const draft = pendingSend;
    setGuardBusy(true);
    try {
      const newKey = await createSession();
      setPendingSend(null);
      if (!newKey) return; // no logged-in user — nothing sensible to do
      // activeSessionId hasn't propagated through React yet, so target the
      // new thread explicitly. Mentions auto-invite into the NEW thread —
      // the user owns it, so the ACL mutation is allowed.
      sendMessage(draft.content, draft.files, { sessionKeyOverride: newKey });
      if (draft.mentions && draft.mentions.length > 0) {
        autoInviteMentions(newKey, draft.mentions);
      }
    } finally {
      setGuardBusy(false);
    }
  }, [pendingSend, createSession, sendMessage, autoInviteMentions]);

  // ── Slash-command intercept: /wrong:, /feedback: ────────────────────────────
  // Flags the most-recent Foreman (assistant) message on the active thread.
  // We look at `messages` in its current order — the gateway pushes new
  // turns onto the end, so the rightmost foreman entry is the one the user
  // is reacting to. Corner case: if there's no Foreman turn yet (brand-new
  // thread, user fired /wrong: as the first message), we POST with no
  // messageId — the server stores it as a freestanding thread-level
  // complaint, which is still useful triage signal.
  const handleFeedbackCommand = useCallback(
    async (reason: string): Promise<{ ok: boolean; error?: string }> => {
      if (!activeSessionId) {
        return { ok: false, error: 'No active thread.' };
      }
      let lastForemanMessageId: string | null = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].type === 'foreman') {
          lastForemanMessageId = messages[i].id;
          break;
        }
      }
      try {
        const token = localStorage.getItem('foreman-auth-token') || '';
        const res = await fetch('/api/auth/feedback/report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sessionKey: activeSessionId,
            messageId: lastForemanMessageId,
            category: 'wrong',
            reason,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          return { ok: false, error: data.error || `HTTP ${res.status}` };
        }
        return { ok: true };
      } catch (err) {
        return { ok: false, error: (err as Error).message || 'Network error' };
      }
    },
    [activeSessionId, messages]
  );

  const handleQuickAction = useCallback(
    (prompt: string) => {
      sendMessage(prompt);
    },
    [sendMessage]
  );

  const handleCorrect = useCallback(
    (messageId: string, data: CorrectionFormData) => {
      submitCorrection(messageId, data.correctedValue, data.category);
    },
    [submitCorrection]
  );

  const handleAction = useCallback(
    (action: string) => {
      sendMessage(action);
    },
    [sendMessage]
  );

  const handleDeleteThread = useCallback(
    async (sessionKey: string) => {
      try {
        await deleteAclRow(sessionKey);
        // If we just deleted the OPEN thread, switch to the newest remaining
        // one (sessions is sorted lastMessage-desc) — otherwise the deleted
        // thread's content stays on screen and compose keeps targeting it.
        // Route through switchSession, the single source of truth; null when
        // nothing remains (safe empty state).
        if (activeSessionId === sessionKey) {
          const remaining = sessions.filter((s) => s.id !== sessionKey);
          switchSession(remaining.length > 0 ? remaining[0].id : null);
        }
        await refreshAcl();
      } catch (err) {
        console.error('[App] deleteThread failed:', err);
      }
    },
    [activeSessionId, sessions, deleteAclRow, refreshAcl, switchSession]
  );

  const handleShareThread = useCallback((sessionKey: string) => {
    const target = acl.find((a) => a.sessionKey === sessionKey);
    if (!target) return;
    setShareTarget(target);
  }, [acl]);

  const handleRenameThread = useCallback(
    async (sessionKey: string) => {
      const target = acl.find((a) => a.sessionKey === sessionKey);
      const current = target?.name || '';
      const next = window.prompt('Rename thread:', current);
      if (next === null) return;
      const trimmed = next.trim();
      if (!trimmed || trimmed === current) return;
      try {
        await renameAcl(sessionKey, trimmed.slice(0, 120));
      } catch (err) {
        console.error('[App] renameThread failed:', err);
        window.alert((err as Error).message || 'Rename failed');
      }
    },
    [acl, renameAcl]
  );

  const handleShareSave = useCallback(
    async (sharedWith: string[] | '*') => {
      if (!shareTarget) return;
      await shareAcl(shareTarget.sessionKey, sharedWith);
      setShareTarget(null);
    },
    [shareTarget, shareAcl]
  );

  const cfg = getRoleConfig(role);

  const chatView = (
    <>
      <div className={styles.main}>
        <Sidebar
          myThreads={myThreads}
          sharedWithMe={sharedWithMe}
          activeThreadId={activeThreadId}
          onSelectThread={handleSelectThread}
          onDeleteThread={handleDeleteThread}
          onShareThread={handleShareThread}
          onRenameThread={handleRenameThread}
          currentRole={role}
          isAdmin={isAdmin}
          sidebarTab={sidebarTab}
          onTabChange={setSidebarTab}
          knowledgeEntries={knowledgeEntries}
          onSearchKnowledge={searchKnowledge}
          sidebarOpen={sidebarOpen}
        />
        <div className={styles.chat}>
          <div className={styles.blueprintBg} aria-hidden />
          <ChatArea
            messages={messages}
            isStreaming={isStreaming}
            role={role}
            userName={user?.name}
            activeSessionId={activeSessionId}
            isWelcomeThread={!!user?.username && activeSessionId === `welcome:${user.username}`}
            emails={threadEmails}
            isSharedThread={activeThreadShared || !activeThreadOwnedByMe}
            currentUsername={user?.username}
            onCorrect={handleCorrect}
            onAction={handleAction}
            onQuickAction={handleQuickAction}
          />
          {guardActive && (
            <ComposeGuard
              ownerName={ownerDisplayName}
              pending={pendingSend}
              onPostAnyway={handleGuardPostAnyway}
              onStartOwnThread={handleGuardStartOwnThread}
              busy={guardBusy}
            />
          )}
          <InputBar
            onSend={handleSend}
            teachMode={teachMode}
            onToggleTeachMode={toggleTeachMode}
            disabled={status !== 'connected'}
            roster={roster}
            currentUsername={user?.username}
            activeSessionId={activeSessionId}
            onFeedbackCommand={handleFeedbackCommand}
          />
        </div>
      </div>
      {shareTarget && (
        <ShareModal
          thread={shareTarget}
          roster={roster}
          onCreateInviteLink={createInviteLink}
          onSave={handleShareSave}
          onClose={() => setShareTarget(null)}
        />
      )}
    </>
  );

  return (
    <div className={styles.app} data-accent={cfg.accent}>
      <TopBar
        connectionStatus={status}
        currentRole={role}
        onRoleChange={setRole}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onCreateSession={createSession}
        onSwitchSession={switchSession}
        onToggleSidebar={toggleSidebar}
        isAdmin={isAdmin}
        onOpenAdmin={() => setAdminPanelOpen(true)}
        onOpenFeedback={() => setFeedbackPanelOpen(true)}
        openFeedbackCount={openFeedbackCount}
        onOpenSettings={() => setSettingsPanelOpen(true)}
        onPrintChat={handlePrint}
      />
      <Routes>
        <Route path="/" element={chatView} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {adminPanelOpen && isAdmin && (
        <AdminUserPanel onClose={() => setAdminPanelOpen(false)} />
      )}
      {isAdmin && (
        <FeedbackTriagePanel
          open={feedbackPanelOpen}
          onClose={() => setFeedbackPanelOpen(false)}
        />
      )}
      {settingsPanelOpen && (
        <BrowserUseKeyPanel onClose={() => setSettingsPanelOpen(false)} />
      )}
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.app} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--muted)', fontSize: 14 }}>Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AuthenticatedApp />;
}
